import { ContentParser } from '@common/components/ContentParser';
import { analytics } from '@common/features/analytics';
import useRoomCapacityText from '@common/hooks/useRoomCapacityText';
import { useTranslations } from '@lang';
import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock';
import { FC, useCallback } from 'react';
import { Link } from 'shared-routing';
import { Price as PriceType } from 'shared-types/Common';
import { Beds } from 'shared-types/Room';
import { BackgroundImgixWrapper, Button, ContentModal, Price } from '../../../../components';
import { MoreInfoBox } from '../../../../components/MoreInfoBox';
import {
  ActualPrice,
  AmenitiesList,
  ContentWrapper,
  Controls,
  ExtraAmount,
  GreyText,
  InfoWrapper,
  PriceWrapper,
  RoomName,
  TitlePriceWrapper,
} from './UpgradeItem.styles';

type UpgradeItemProps = {
  isOpen: boolean;
  imageUrl: string | null;
  name: string;
  priceDifference: PriceType;
  pricePerStay: PriceType;
  description: string;
  maxOccupancy: number;
  maxAdults: number | null;
  maxChildren: number | null;
  beds?: Beds;
  linkRoute: string;
  linkParams: any;
  upgradeLinkParams: any;
  id: string;
  amenities?: string[] | null;
  trackingItem?: {
    id: string;
    name: string;
  };
};

export const UpgradeItem: FC<UpgradeItemProps> = ({
  isOpen,
  imageUrl,
  name,
  priceDifference,
  pricePerStay,
  description,
  maxOccupancy,
  maxAdults,
  maxChildren,
  beds,
  linkParams,
  upgradeLinkParams,
  linkRoute,
  amenities,
  trackingItem,
}) => {
  const { t } = useTranslations();
  const targetRef = useCallback((ref) => {
    if (ref) {
      disableBodyScroll(ref);
    } else {
      clearAllBodyScrollLocks();
    }
  }, []);

  const roomCapacityText = useRoomCapacityText({ maxAdults, maxChildren, maxOccupancy });

  const hasAcceptedTracking = (accepted: boolean) =>
    analytics.track({
      event: 'upgrade_selection',
      params: {
        items: [
          {
            original_item_name: trackingItem?.name || 'unknown',
            original_item_id: trackingItem?.id || 'unknown',
            item_name: name,
            item_id: upgradeLinkParams.id,
            price_difference: priceDifference,
            price_per_stay: pricePerStay,
            accepted,
          },
        ],
      },
    });

  return isOpen ? (
    <ContentModal>
      <ContentWrapper ref={targetRef}>
        <BackgroundImgixWrapper src={imageUrl} customHeight="240px" hasTopBorderRadius />
        <InfoWrapper>
          <GreyText>{t('room.upgradeText')}</GreyText>
          <TitlePriceWrapper>
            <RoomName>{name}</RoomName>
            <PriceWrapper>
              <ExtraAmount>
                + <Price price={priceDifference} />
              </ExtraAmount>
              <ActualPrice>
                <Price price={pricePerStay} /> {t('room.night')}
              </ActualPrice>
            </PriceWrapper>
          </TitlePriceWrapper>
          <GreyText>
            {roomCapacityText}
            {beds?.types?.map((bed) => ` | ${bed.quantity} ${bed.name}`)}
          </GreyText>
          <MoreInfoBox>
            <ContentParser text={description} escapeHtml={false} />
            {amenities ? (
              <>
                <b>{t('room.upgrade.amenitiesTitle')}</b>
                <AmenitiesList>
                  {amenities.map((amenity) => (
                    <li key={amenity}>{amenity}</li>
                  ))}
                </AmenitiesList>
              </>
            ) : null}
          </MoreInfoBox>
        </InfoWrapper>
      </ContentWrapper>
      <Controls>
        <Link route={linkRoute} params={linkParams}>
          <Button onClick={() => hasAcceptedTracking(false)}>{t('room.upgrade.noThanks')}</Button>
        </Link>
        <Link route={linkRoute} params={upgradeLinkParams} shallow={false}>
          <Button onClick={() => hasAcceptedTracking(true)} isPrimary>
            {t('room.upgrade')}
          </Button>
        </Link>
      </Controls>
    </ContentModal>
  ) : null;
};
